/*************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/

import React from 'react';
import SearchUtils from '../../../utils/SearchUtils';
import './SearchDropDownRecommendationItem.scss';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import { setSearchQuery, setTempSearchQuery, showSearchDropdown } from '../../../actions/searchActions';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import {
	CONTENT_TYPE,
	DEEPLINK_ROUTES_PARAMS,
	PRESEARCH_UI_SORT_ORDERS,
	SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS,
} from '../../../containers/SearchResultsPage/SearchConstants';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import GlobalsUtils from '../../../utils/GlobalsUtils';

type OwnProps = {
	type: string;
	data: Record<string, any>;
	index: number;
	count: number;
};

type SearchDropDownRecommendationItemProps = OwnProps & WrappedComponentProps & PropsFromRedux;

type State = {
	shouldShowLaunchButton: boolean;
};
export class SearchDropdownRecommendationItem extends ClickBase<SearchDropDownRecommendationItemProps, State> {
	searchHostContext: HostContext;
	private analytics: SearchAnalytics;
	private itemRef: React.RefObject<HTMLDivElement>;
	private appMetadata: AppResultItem;
	constructor(props: SearchDropDownRecommendationItemProps) {
		super(props);
		this.state = {
			shouldShowLaunchButton: false,
		};
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		this.itemRef = React.createRef();
		this.handleClick = this.handleClick.bind(this);
		const { ffcId } = this.props.data;

		if (ffcId) {
			this.appMetadata = this.props.searchAppMetadataList?.apps
				? this.props.searchAppMetadataList.apps[ffcId]
				: {};
		}
	}

	componentDidMount() {
		SearchUtils.checkIfHostContextCanLaunchApp({
			hostContext: this.searchHostContext,
			appMetadata: this.appMetadata,
		})
			.then((canLaunch) => {
				this.setState({
					shouldShowLaunchButton: canLaunch,
				});
			})
			.catch(() => {
				this.setState({
					shouldShowLaunchButton: false,
				});
			});
	}

	getPopularSearchTerms(): Record<string, any> {
		return this.props.presearchResultsPayload?.popularSearches?.searchResults;
	}

	getDisplayName(): string {
		const { type, ffcId, name } = this.props.data;
		if (type === 'apps') {
			const appTitle = this.appMetadata?.name;
			return appTitle;
		}

		return name;
	}
	/*
	 * Returns the appropriate app type
	 * @return {string}
	 */
	getAppType(): string {
		let result = '';
		const appTitle = `${this.appMetadata?.name}`;
		const appType = this.appMetadata?.type;

		if (appType) {
			result = appType;
		} else {
			const extractedAppType = appTitle.match(/\((.*?)\)/);

			if (Array.isArray(extractedAppType) && extractedAppType.length > 1) {
				result = extractedAppType[1];
			}
		}
		result = result.toLowerCase();
		switch (result) {
			case 'service':
				result = 'web';
				break;
			case 'mobile app':
				result = 'mobile';
				break;
		}

		return result;
	}
	getIcon(): string {
		const { type, ffcId, icon, iconDark } = this.props.data;
		if (type === 'apps') {
			return SearchUtils.getAppIconUrl(this.appMetadata?.iconUrls);
		}
		if (type === 'plugins' && !icon && !iconDark) {
			return SearchUtils.getGenericRenditions(type);
		}
		return SearchUtils.isDarkMode(this.props.theme) ? iconDark || icon : icon || iconDark;
	}

	getDescription(): string {
		const { type, ffcId, description } = this.props.data;
		if (type === 'apps') {
			return GlobalsUtils.removePeriodFromEnd(this.appMetadata?.description);
		}
		return description;
	}
	getActionLabel(): string {
		const {
			type,
			ffcId,
			action: { label },
		} = this.props.data;
		if (type === 'apps') {
			let buttonStringId = 'app.search.learn.more';
			if (this.state.shouldShowLaunchButton) {
				buttonStringId = 'app.search.open';
				if (this.appMetadata?.type === 'Service') {
					buttonStringId = 'app.search.launch';
				}
			}
			return this.props.intl.formatMessage({
				id: buttonStringId,
			});
		}
		return label;
	}
	onClick(event: any): void {
		if (event.which === 13 || event.keyCode === 13 || event.type === 'click') {
			const {
				id,
				action: { url, deeplinkParams },
				type,
				ffcId,
				deeplink,
			} = this.props.data;
			const pathName = window.location && window.location.pathname;
			const isSearchRoute = pathName ? pathName.indexOf('search') > -1 : false;

			let openUrl = url ? url : undefined;
			let itemId = id ? id : undefined;
			if (ffcId) {
				openUrl = this.appMetadata?.url;
				itemId = this.appMetadata?.id;
			}

			const exitButtonElem = document.getElementById('search-exit-button');

			const clickResultItem: ClickResult = {
				action: 'open',
				item: {
					id: itemId,
					// @ts-ignore
					type,
					attributes: {
						name: this.getDisplayName(),
						modificationDate: Date.now(),
						creationDate: Date.now(),
						fileSize: 0,
						assetCategory: type as AssetCategory,
						openUrl,
					},
				},
			};
			// log quick links click event to ingest
			this.analytics.postPreSearchItemAction({
				eventIndex: this.props.index,
				recommendationType: 'appRecommendations',
				fromModel: !this.getPopularSearchTerms().isEmpty,
				isExpress: this.getPopularSearchTerms().isExpress,
				planGroup: this.getPopularSearchTerms().planGroup,
				modelVersion: this.getPopularSearchTerms().modelVersion,
				eventType: 'click',
				uiSortOrder: PRESEARCH_UI_SORT_ORDERS.appRecommendations,
				data: [Object.assign({}, this.props.data, { actionUrl: openUrl, id: itemId })],
			});

			type === 'apps' &&
				this.analytics.postLaunchOfApp(
					`${this.getDisplayName()} (${this.getAppType()})`,
					openUrl,
					CONTENT_TYPE.LEARN_MORE,
					SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_QUICK_LINK,
					this.props.searchQueryValue,
				);

			if (deeplink === true && deeplinkParams) {
				SearchUtils.routeDeepLinksInCCD({
					searchHostContext: this.searchHostContext,
					deepLinkParams: deeplinkParams,
					itemType: type,
				}).then((result) => {
					if (!result) {
						this.onClickCard(this.searchHostContext, type, clickResultItem);
					}
					if (exitButtonElem && !isSearchRoute) {
						// analytics.initSearchAnalytics(item.label);
						exitButtonElem.click();
					}
				});
			} else {
				this.onClickCard(this.searchHostContext, type, clickResultItem);
				if (exitButtonElem && !isSearchRoute) {
					// analytics.initSearchAnalytics(item.label);
					exitButtonElem.click();
				}
			}

			event.stopPropagation();
		}
	}
	handleAppClick(event: any): void {
		if (this.state.shouldShowLaunchButton && typeof this.searchHostContext.launchApp === 'function') {
			SearchUtils.executeAppLaunch({
				searchHostContext: this.searchHostContext,
				appMetadata: this.appMetadata,
				searchAppMetadataList: this.props.searchAppMetadataList,
			});
			event.stopPropagation();
			// log quick links click event to ingest
			this.analytics.postPreSearchItemAction({
				eventIndex: this.props.index,
				recommendationType: 'appRecommendations',
				fromModel: !this.getPopularSearchTerms().isEmpty,
				isExpress: this.getPopularSearchTerms().isExpress,
				planGroup: this.getPopularSearchTerms().planGroup,
				modelVersion: this.getPopularSearchTerms().modelVersion,
				eventType: 'click',
				uiSortOrder: PRESEARCH_UI_SORT_ORDERS.appRecommendations,
				data: [
					Object.assign({}, this.props.data, {
						actionUrl: this.appMetadata?.serviceLaunchUrl || this.appMetadata?.url,
					}),
				],
			});

			this.analytics.postLaunchOfApp(
				`${this.getDisplayName()} (${this.getAppType()})`,
				this.appMetadata?.type === 'Service' ? this.appMetadata?.serviceLaunchUrl : this.appMetadata?.url,
				this.appMetadata?.type === 'Service' ? 'Launch' : 'Open',
				SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_QUICK_LINK,
				this.props.searchQueryValue,
			);
		} else {
			this.onClick(event);
		}
	}
	handleClick(event: any): void {
		if (event.which === 13 || event.keyCode === 13 || event.type === 'click') {
			const { type } = this.props.data;
			this.itemRef.current && this.itemRef.current.blur();
			if (type === 'apps') {
				this.handleAppClick(event);
			} else {
				this.onClick(event);
			}
		}
	}

	render() {
		const { type } = this.props.data;
		if (type === 'apps' && !this.appMetadata) {
			return null;
		}
		return (
			<div
				className='search-dropdown-recommendation-item'
				ref={this.itemRef}
				id={`search-dropdown-recommendation-item-${this.props.index}`}
				tabIndex={-1}
				onClick={this.handleClick}
				role='option'
			>
				<div className='search-dropdown-recommendation-item-text-container'>
					<div className='search-dropdown-recommendation-item-icon-container'>
						<img
							className='search-dropdown-recommendation-item-icon'
							alt={this.getDisplayName()}
							src={this.getIcon()}
						/>
					</div>

					<div className='search-dropdown-recommendation-item-text-title-container'>
						<div className='search-dropdown-recommendation-item-text-title'>
							<span>{this.getDisplayName()}</span>
						</div>
						<div className='search-dropdown-recommendation-item-text-type'>
							<span>{this.getDescription()}</span>
						</div>
					</div>
					<span className='search-dropdown-recommendation-item-text-launch-button'>
						{this.getActionLabel()}
					</span>
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchConfig: search.searchConfig,
	presearchResultsPayload: search.presearchResultsPayload,
	searchAppMetadataList: search.searchAppMetadataList,
	searchQueryValue: search.searchQuery,
	theme: search.theme,
});

const mapDispatchToProps = {
	setSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropdownRecommendationItem));
